import cls from "classnames"
import { animate } from "animejs"
import React, { FC, useEffect, useRef, useState } from "react"

import { useActivity, useOwnTeam, useTask } from "@hg/recall-frame"
import { i18n } from "@/i18n"
import Toast from "@/components/Toast"
import OutlineButton from "@/components/OutlineButton"

import styles from "./index.module.scss"

type TeamMember = {
    uid: string
    nickname: string | null
    avatar: string | null
}

type RawAvatar = { url?: string } | string | null | undefined

const resolveAvatar = (raw: RawAvatar): string | null => {
    if (!raw) return null
    if (typeof raw === "string") return raw
    return raw.url ?? null
}

export const Progress: FC = () => {
    const teamSwr = useOwnTeam()
    const taskSwr = useTask()
    const activityStore = useActivity()

    const [teamCode, setTeamCode] = useState<string | null>(null)
    const [member, setMember] = useState<TeamMember | null | undefined>(undefined)
    const [taskProgress, setTaskProgress] = useState(0)
    const [taskTotal, setTaskTotal] = useState(0)

    useEffect(() => {
        const team = teamSwr.data
        if (!team) return
        setTeamCode(team.code)
        const raw = team.member
        setMember(
            raw
                ? {
                      uid: raw.uid,
                      nickname: raw.nickname ?? null,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      avatar: resolveAvatar((raw as any).avatar),
                  }
                : null,
        )
    }, [teamSwr.data])

    useEffect(() => {
        const tasks = taskSwr.data
        const activity = activityStore.data
        if (!tasks || !activity) return
        const memberLoginTask = activity.tasks.find(
            (t) => t.template === "member_login_times",
        )
        const total = (memberLoginTask?.templateParams?.total as number) ?? 0
        const taskStatus = tasks.find((t) => t.cid === memberLoginTask?.cid)
        setTaskTotal(total)
        setTaskProgress(taskStatus?.progress?.value ?? 0)
    }, [taskSwr.data, activityStore.data])

    if (member) {
        return (
            <div className={styles.progress}>
                <MemberInfo
                    current={taskProgress}
                    total={taskTotal}
                    uid={member.uid}
                    memberNickName={member.nickname ?? "unknown"}
                    avatar={member.avatar ?? ""}
                />
            </div>
        )
    }

    return (
        <div className={styles.progress}>
            <Empty inviteCode={teamCode ?? ""} unsatisfied={false} />
        </div>
    )
}

// ─── MemberInfo ───────────────────────────────────────────────────────────────

const progressTransitionDuration = 1000

const MemberInfo: FC<{
    current: number
    total: number
    uid: string
    memberNickName: string
    avatar: string
}> = ({ current, total, uid, memberNickName, avatar }) => {
    const barRef = useRef<HTMLDivElement>(null)
    const valueRef = useRef(0)

    useEffect(() => {
        if (current) {
            const anim = animate(valueRef, {
                current: Math.min(1, current / total),
                ease: "easeInOutQuad",
                duration: progressTransitionDuration,
                onUpdate: () => {
                    if (barRef.current) {
                        barRef.current.style.width =
                            (100 * valueRef.current).toFixed(2) + "%"
                    }
                },
            })
            return () => {
                anim?.pause?.()
            }
        }
    }, [current, total])

    return (
        <>
            <div className={styles.info}>
                <div className={styles.left}>
                    <div className={styles.avatar}>
                        <div className={styles.inner}>
                            <img src={avatar} alt={memberNickName} />
                        </div>
                    </div>
                </div>
                <div className={styles.right}>
                    <div className={styles.uid}>
                        <div className={styles.wrap}>
                            <div className={styles.label}>uid</div>
                            <div className={styles.number}>{uid}</div>
                        </div>
                    </div>
                    <div className={styles.intro}>
                        <div
                            className={cls(
                                styles.wrap,
                                memberNickName.length > 17 && styles.longestNickname,
                            )}
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                            dangerouslySetInnerHTML={{
                                __html: i18n.getRecall("progress.inviter", {
                                    nickname: memberNickName,
                                }),
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.bars}>
                <div className={styles.progressBar}>
                    <div className={styles.barInner}>
                        <div className={styles.loadedBar} ref={barRef}>
                            <div className={styles.pointer} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.steps}>
                <div className={styles.value}>
                    <div
                        className={styles.format}
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                        dangerouslySetInnerHTML={{
                            __html: i18n.getRecall("progress.value_steps", {
                                num: current,
                                total,
                            }),
                        }}
                    />
                </div>
                <div className={styles.intro}>
                    {i18n.getRecall("progress.total_steps_intro", { total })}
                </div>
            </div>
        </>
    )
}

// ─── Empty ────────────────────────────────────────────────────────────────────

const Empty: FC<{
    unsatisfied?: boolean
    inviteCode: string
}> = ({ inviteCode, unsatisfied }) => {
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(inviteCode)
            Toast.message(i18n.getRecall("message.copy_success"))
        } catch {
            Toast.message(i18n.getRecall("message.copy_fail"))
        }
    }

    return (
        <div className={styles.empty}>
            <div
                className={styles.slogan}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                dangerouslySetInnerHTML={{
                    __html: unsatisfied
                        ? i18n.getRecall("progress.text_unsatisfied")
                        : i18n.getRecall("progress.text_noMember"),
                }}
            />
            {!unsatisfied && (
                <>
                    <div className={styles.code}>
                        <div className={styles.label}>
                            {i18n.getRecall("progress.text_inviteCode")}
                        </div>
                        <div className={styles.cont}>{inviteCode}</div>
                    </div>
                    <OutlineButton
                        className={styles.copyBtn}
                        onClick={copy}
                        text={i18n.getRecall("progress.text_copy")}
                        fontSize={36}
                        fillGradient={{ from: "#FEFAF6", to: "#FDF2CF" }}
                        strokeColor="#1B6472"
                        strokeWidth={8}
                    />
                </>
            )}
        </div>
    )
}
